import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import SettingSection from "@/modules/shared/SettingSection"
import SettingContentWrapper from "@/modules/shared/SettingContentWrapper"
import { Form, Input, Radio, Select, Switch, message } from "antd"
import { Loader, SecondaryText, SemiBoldLabelText } from "@/UI"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import FormItem from "antd/lib/form/FormItem"
import { getOptimizationConfig, putOptimizationConfig } from "@/api/optimization"
import {
  OptimizationBalanceBy,
  OptimizationConfig,
  OptimizationMethod,
  ZoneOptimizationConfig,
  defaultOptimizationConfig,
} from "@/types/optimization"
import { useRecoilValue } from "recoil"
import { isPayingUserSelector, userAtom } from "@/atoms/userAtom"
import { MAX_STOPS_PER_ORDER } from "@/types/orders.types"
import { useFlags } from "flagsmith/react"
import MenuTabTag from "../shared/MenuTabTag.component"
import { LabelWithTooltip } from "@/UI/Labels/LabelWithTooltip"

interface RouteSettingsProps {}

const RouteSettings: React.FC<RouteSettingsProps> = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const { org } = useRecoilValue(userAtom)
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const [optimizationConfig, setOptimizationConfig] =
    useState<OptimizationConfig>(defaultOptimizationConfig)
  const [isModifying, setIsModifying] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPostLoading, setPostIsLoading] = useState<boolean>(false)
  const optimizationMethod = Form.useWatch("optimizationMethod", form)
  const flags = useFlags([
    "show_optimization_balance_methods",
    "show_optimization_enable_multiple_pickups",
    "nash_edg_demo",
  ])

  const watchZones = Form.useWatch("zones", form)

  const fetchOptimizationConfig = async () => {
    setIsLoading(true)
    try {
      const { data } = await getOptimizationConfig()
      setOptimizationConfig(data)
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchOptimizationConfig()
  }, [org?.id])

  const resetForm = () => {
    const maxShiftTime = {
      hours: optimizationConfig?.maxShiftTime
        ? Math.floor(optimizationConfig.maxShiftTime / 60)
        : 0,
      minutes: optimizationConfig?.maxShiftTime ? optimizationConfig.maxShiftTime % 60 : 0,
    }
    form.setFieldsValue({
      ...optimizationConfig,
      maxShiftTime,
      maxDistanceInKm: optimizationConfig?.maxDistanceInMeters
        ? optimizationConfig?.maxDistanceInMeters / 1000
        : 0,
    })
  }

  useEffect(() => {
    resetForm()
  }, [optimizationConfig])

  const endLocationOptions = [
    {
      label: t("profile.routeSettings.dontUseEndLocation"),
      value: false,
    },
    {
      label: t("profile.routeSettings.useEndLocation"),
      value: true,
    },
  ]

  const volumetricCapacityOptions = [
    {
      label: t("profile.routeSettings.dontUseVolumetricCapacity"),
      value: false,
    },
    {
      label: t("profile.routeSettings.useVolumetricCapacity"),
      value: true,
    },
  ]

  const zonesOptions = [
    {
      label: t("profile.RouteSettings.disableZones"),
      value: ZoneOptimizationConfig.DISABLED,
    },
    {
      label: t("profile.RouteSettings.useStrictZones"),
      value: ZoneOptimizationConfig.STRICT,
    },
    {
      label: t("profile.RouteSettings.usePreferredZones"),
      value: ZoneOptimizationConfig.PREFERRED,
    },
  ]

  const optimizationOptions = [
    {
      label: t("profile.RouteSettings.deliverFewDrivers"),
      value: "minimized",
    },
    {
      label: t("profile.RouteSettings.distributeStops"),
      value: "balanced",
    },
  ]

  const optimizationBalanceOptions = [
    {
      label: t("profile.RouteSettings.balanceStops"),
      value: OptimizationBalanceBy.STOPS,
    },
    {
      label: t("profile.RouteSettings.balanceTime"),
      value: OptimizationBalanceBy.TIME,
    },
    {
      label: t("profile.RouteSettings.mostEfficient"),
      value: OptimizationBalanceBy.EFFICIENCY,
    },
  ]

  const onFinish = async (
    formValues: OptimizationConfig & {
      maxShiftTime: {
        hours: string
        minutes: string
      }
      maxDistanceInKm: string
    }
  ) => {
    setPostIsLoading(true)
    try {
      const { data } = await putOptimizationConfig({
        ...formValues,
        maxBatchSize: Number(formValues.maxBatchSize),
        maxDistanceInMeters: Math.floor(Number(formValues.maxDistanceInKm) * 1000),
        maxShiftTime:
          Number(formValues.maxShiftTime.hours) * 60 + Number(formValues.maxShiftTime.minutes),
        howLongToWaitAtLocation: Number(formValues.howLongToWaitAtLocation),
        delayWindow: Number(formValues.delayWindow),
        zones: formValues.zones,
        routesWithinZone:
          formValues?.zones !== ZoneOptimizationConfig.DISABLED
            ? formValues.routesWithinZone
            : false,
        optimizationMethod: formValues.optimizationMethod,
        enableMultiplePickups: formValues.enableMultiplePickups,
      })
      setOptimizationConfig(data)
      message.success(t("common.form.updatedPreferencesSuccess"))
    } catch (err) {
      message.error(t("common.form.updatedPreferencesFailure"))
    }
    setPostIsLoading(false)
  }

  return (
    <SettingSection
      title={t("profile.common.routeSettings")}
      actionButton={
        <div className="flex gap-x-4">
          <KosmoButton
            onClick={() => {
              resetForm()
              setIsModifying(false)
            }}
            type="default"
            classname="font-semibold"
            isDisabled={!isModifying || isPostLoading}
          >
            {t("common.cancel")}
          </KosmoButton>
          <KosmoButton
            type="primary"
            classname="font-semibold"
            htmlType="submit"
            onClick={() => form.submit()}
            isLoading={isPostLoading}
            isDisabled={!isModifying}
          >
            {t("common.save")}
          </KosmoButton>
        </div>
      }
    >
      <SettingContentWrapper>
        {isLoading ? (
          <Loader fontSize={40} size="small" />
        ) : (
          <Form
            id="route-settings-form"
            className="flex flex-col w-full gap-y-2"
            form={form}
            onFinish={onFinish}
            onFieldsChange={() => setIsModifying(true)}
            disabled={!isPayingUser}
          >
            <Form.Item name="optimizationMethod">
              <div className="flex flex-col gap-y-1">
                <SemiBoldLabelText>{t("profile.RouteSettings.howToOptimize")}</SemiBoldLabelText>
                <Radio.Group value={optimizationMethod}>
                  {optimizationOptions.map((option) => (
                    <Radio key={option.value} value={option.value} style={{ display: "block" }}>
                      {option.label}
                    </Radio>
                  ))}
                </Radio.Group>
              </div>
            </Form.Item>
            {flags.show_optimization_balance_methods.enabled &&
              optimizationMethod === OptimizationMethod.BALANCED && (
                <div className="flex flex-col gap-y-1">
                  <SemiBoldLabelText>{t("profile.RouteSettings.howToBalance")}</SemiBoldLabelText>
                  <FormItem name="balanceBy">
                    <Radio.Group>
                      {optimizationBalanceOptions.map((option) => (
                        <Radio key={option.value} value={option.value} style={{ display: "block" }}>
                          {option.label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </FormItem>
                </div>
              )}
            {flags.show_optimization_enable_multiple_pickups.enabled && (
              <div className="flex flex-col gap-y-1">
                <div className="flex">
                  <SemiBoldLabelText>
                    {t("profile.RouteSettings.enableMultiplePickupRoutes")}
                  </SemiBoldLabelText>
                  <MenuTabTag type={"beta"} />
                </div>
                <Form.Item name="enableMultiplePickups">
                  <Switch
                    defaultValue={optimizationConfig.enableMultiplePickups}
                    onChange={() => {
                      form.setFieldsValue({
                        enableMultiplePickups: !optimizationConfig.enableMultiplePickups,
                      })
                      setIsModifying(true)
                    }}
                  />
                  <div className="rounded-md bg-gray-50 px-4 overflow-hidden mt-4 p-2">
                    <SecondaryText>
                      {t("profile.RouteSettings.enableMultiplePickupRoutesDesc")}
                    </SecondaryText>
                  </div>
                </Form.Item>
              </div>
            )}
            <div className="flex flex-col gap-y-1">
              <LabelWithTooltip
                label={t("profile.RouteSettings.endLocation")}
                tooltipContent={t("profile.RouteSettings.endLocationTooltip")}
              />
              <FormItem name="shouldEndAtPickupLocation">
                <Select
                  className="min-w-[280px]"
                  options={endLocationOptions}
                  defaultValue={false}
                />
              </FormItem>
            </div>
            <div className="flex flex-col gap-y-1">
              <LabelWithTooltip
                label={t("profile.RouteSettings.volumetricCapacity")}
                tooltipContent={t("profile.RouteSettings.volumetricCapacityTooltip")}
              />
              <FormItem name="shouldConsiderVolumetricCapacity">
                <Select
                  className="min-w-[280px]"
                  options={volumetricCapacityOptions}
                  defaultValue={false}
                />
              </FormItem>
            </div>
            <div className="flex flex-col gap-y-1">
              <LabelWithTooltip
                label={t("profile.RouteSettings.maximumStopsPerRoute")}
                tooltipContent={t("profile.RouteSettings.maximumStopsPerRouteTooltip")}
              />
              <FormItem name="maxBatchSize" style={{ marginBottom: "0.7em" }}>
                <Input
                  name="maxBatchSize"
                  className="border-[#d9d9d9] !max-w-[120px]"
                  placeholder={t("common.stops")}
                  max={MAX_STOPS_PER_ORDER}
                  step={1}
                  min={0}
                  suffix={<></>}
                  type="number"
                />
              </FormItem>
            </div>
            <div className="flex flex-col gap-y-1">
              <LabelWithTooltip
                label={t("profile.RouteSettings.maximumDistance")}
                tooltipContent={t("profile.RouteSettings.maximumDistanceTooltip")}
              />
              <FormItem name="maxDistanceInKm">
                <Input
                  name="maxDistanceInKm"
                  className="!max-w-[120px]"
                  min={0}
                  step={0.01}
                  suffix={t("common.kilometresAbbreviation")}
                  type="number"
                />
              </FormItem>
            </div>
            <div className="flex flex-col gap-y-1">
              <LabelWithTooltip
                label={t("profile.RouteSettings.maxShiftTime")}
                tooltipContent={t("profile.RouteSettings.maxShiftTimeTooltip")}
              />
              <div className="flex gap-x-2">
                <FormItem name={["maxShiftTime", "hours"]}>
                  <Input
                    className="!max-w-[80px]"
                    suffix={t("common.hoursAbbreviation")}
                    min={0}
                    style={{ borderRadius: "none !important" }}
                  />
                </FormItem>
                <FormItem name={["maxShiftTime", "minutes"]}>
                  <Input
                    className="!max-w-[100px]"
                    suffix={t("common.minutesAbbreviation")}
                    min={0}
                    max={59}
                    style={{ borderRadius: "none !important" }}
                  />
                </FormItem>
              </div>
            </div>
            <div className="flex flex-row gap-x-12">
              <div className="flex flex-col gap-y-1">
                <LabelWithTooltip
                  label={t("profile.RouteSettings.avgTimeAtStop")}
                  tooltipContent={t("profile.RouteSettings.avgTimeAtStopTooltip")}
                />
                <FormItem name="howLongToWaitAtLocation">
                  <Input
                    name="howLongToWaitAtLocation"
                    className="!max-w-[100px]"
                    placeholder={"5"}
                    type="number"
                    min={0}
                    suffix={t("common.minutesAbbreviation")}
                    style={{ borderRadius: "none !important" }}
                  />
                </FormItem>
              </div>
              {flags.nash_edg_demo?.enabled && (
                <>
                  <div className="flex flex-col gap-y-1">
                    <LabelWithTooltip
                      label={t("profile.RouteSettings.avgVehicleLoadingTime")}
                      tooltipContent={t("profile.RouteSettings.avgVehicleLoadingTimeTooltip")}
                    />
                    <FormItem name="avgVehicleLoadingTime">
                      <Input
                        name="avgVehicleLoadingTime"
                        className="!max-w-[100px]"
                        placeholder={"15"}
                        type="number"
                        min={0}
                        suffix={t("common.minutesAbbreviation")}
                        style={{ borderRadius: "none !important" }}
                      />
                    </FormItem>
                  </div>
                  <div className="flex flex-col gap-y-1">
                    <LabelWithTooltip
                      label={t("profile.RouteSettings.drivingSpeedBuffer")}
                      tooltipContent={t("profile.RouteSettings.drivingSpeedBufferTooltip")}
                    />
                    <FormItem name="drivingSpeedBuffer">
                      <Input
                        name="drivingSpeedBuffer"
                        className="!max-w-[100px]"
                        placeholder={"1.1"}
                        min={0.1}
                        max={2}
                        type="number"
                        suffix={"x"}
                        style={{ borderRadius: "none !important" }}
                      />
                    </FormItem>
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col gap-y-1">
              <LabelWithTooltip
                label={t("profile.RouteSettings.acceptedDelay")}
                tooltipContent={t("profile.RouteSettings.acceptedDelayTooltip")}
              />
              <FormItem name="delayWindow">
                <Input
                  name="delayWindow"
                  className="withAddOnInput !max-w-[100px]"
                  placeholder={"0"}
                  suffix={t("common.minutesAbbreviation")}
                  style={{ borderRadius: "none !important" }}
                />
              </FormItem>
            </div>
            <div className="flex flex-row gap-x-12">
              <div className="flex flex-col gap-y-1">
                <LabelWithTooltip
                  label={t("profile.RouteSettings.zones")}
                  tooltipContent={t("profile.RouteSettings.zonesTooltip")}
                />
                <FormItem name="zones" className="mb-0">
                  <Select
                    className="min-w-[280px]"
                    size="middle"
                    options={zonesOptions}
                    defaultValue={optimizationConfig.zones}
                  />
                </FormItem>
              </div>
              {watchZones !== ZoneOptimizationConfig.DISABLED && (
                <div className="flex flex-col gap-y-1">
                  <LabelWithTooltip
                    label={t("profile.RouteSettings.zonesCannotMix")}
                    tooltipContent={t("profile.RouteSettings.zonesCannotMixTooltip")}
                  />
                  <FormItem name="routesWithinZone">
                    <Switch />
                  </FormItem>
                </div>
              )}
            </div>
            <div className="flex gap-x-4 mt-10">
              <KosmoButton
                onClick={() => {
                  resetForm()
                  setIsModifying(false)
                }}
                type="default"
                classname="font-semibold"
                isDisabled={!isModifying || isPostLoading}
              >
                {t("common.cancel")}
              </KosmoButton>
              <KosmoButton
                type="primary"
                classname="font-semibold"
                htmlType="submit"
                isLoading={isPostLoading}
                isDisabled={!isModifying}
              >
                {t("common.save")}
              </KosmoButton>
            </div>
          </Form>
        )}
      </SettingContentWrapper>
    </SettingSection>
  )
}

export default RouteSettings
